import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertTriangle, Check, Info, X } from "lucide-react";
import { springSnappy } from "@/lib/motion";
import { useI18n } from "@/i18n";

/**
 * 全域回饋
 * ═══════════════════════════════════════════════════════════════
 *
 * 在這之前，每個動作的結果都是就地渲染一行字 —— 送出提案、
 * 收回訊息、上傳頭像各自寫一次。問題有兩個：
 *
 *   使用者按下按鈕之後視線在按鈕上，而回饋出現在別的地方
 *   捲動之後看不到，等於沒有回饋
 *
 * Toast 把回饋固定在視線可及的位置，而且不打斷操作。
 *
 * ── 動畫的取捨 ─────────────────────────────────────────────
 *
 * 進場用 spring 從下方推上來，帶一點 overshoot —— 那個「彈一下」
 * 是它會被注意到的原因。退場刻意更快而且不彈：離開的東西不該
 * 再要求注意力。
 *
 * 多則堆疊時用 layout 動畫，新的推開舊的而不是蓋上去 ——
 * 蓋上去會讓人以為前一則消失了。
 */

type ToastKind = "success" | "error" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

/** 停留時間。錯誤久一點 —— 那通常需要讀完才知道要做什麼。 */
const DURATION: Record<ToastKind, number> = {
  success: 3200,
  info: 4000,
  error: 6000,
};

const ICON: Record<ToastKind, typeof Check> = {
  success: Check,
  error: AlertTriangle,
  info: Info,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;

      /*
       * 最多同時三則。
       *
       * 超過的話最舊的先走 —— 一疊十則 toast 沒有人會讀，
       * 而且會把畫面蓋掉。
       */
      setToasts((current) => [...current.slice(-2), { id, kind, message }]);
      setTimeout(() => dismiss(id), DURATION[kind]);
    },
    [dismiss],
  );

  const value = useMemo<ToastValue>(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("error", message),
      info: (message) => push("info", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  const reduceMotion = useReducedMotion();
  const { t } = useI18n();

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
      role="status"
      aria-live="polite"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const Icon = ICON[toast.kind];

          return (
            <motion.div
              key={toast.id}
              layout
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              // 退場更快且不彈 —— 離開的東西不該再要求注意力
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, scale: 0.96, transition: { duration: 0.16 } }
              }
              transition={springSnappy}
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl nm-lg px-4 py-3.5"
            >
              <span
                className={
                  "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-lg nm-inset-sm " +
                  (toast.kind === "error"
                    ? "text-brand-800 dark:text-brand-300"
                    : "text-brand-800 dark:text-brand-400")
                }
              >
                <Icon className="size-3.5" strokeWidth={2.2} />
              </span>

              <p className="min-w-0 flex-1 text-sm leading-relaxed text-ink">{toast.message}</p>

              <button
                type="button"
                onClick={() => onDismiss(toast.id)}
                aria-label={t("common.close")}
                className="mt-0.5 shrink-0 text-ink-muted transition-colors hover:text-ink-soft"
              >
                <X className="size-3.5" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export function useToast(): ToastValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
